import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ListPagination } from "@/components/list-pagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ENTRIES_PER_PAGE, type EntryFilters as Filters } from "@/features/billing/api";
import { EntryFilters } from "@/features/billing/entry-filters";
import { walletEntriesQuery } from "@/features/billing/queries";
import { readEntriesSearch } from "@/features/billing/search";
import { WalletEntriesTable } from "@/features/billing/wallet-entries-table";
import { m } from "@/lib/paraglide/messages.js";

// /billing: the billing history, a page at a time from the API, under the balance header the
// billing pages share (route.tsx). The kind, the range (this month unless the URL names one) and
// the page are route state (guide, rule 16). Every reseller reads it, pending and suspended ones
// too (rule 17: they keep their billing history).
export const Route = createFileRoute("/_authenticated/billing/")({
  validateSearch: (raw: Record<string, unknown>) => readEntriesSearch(raw),
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    filters: { kind: search.kind, from: search.from, to: search.to } satisfies Filters,
  }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(walletEntriesQuery(deps.page, deps.filters)),
  component: BillingHistory,
});

function BillingHistory() {
  const { page: asked = 1, kind, from, to } = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters: Filters = { kind, from, to };
  const { data: entries } = useSuspenseQuery(walletEntriesQuery(asked, filters));
  const { page, total } = entries.meta;
  const pages = Math.max(1, Math.ceil(total / ENTRIES_PER_PAGE));
  const shown = entries.data.length;
  const first = shown === 0 ? 0 : (page - 1) * ENTRIES_PER_PAGE + 1;
  const range = m.common_page_range({ from: first, to: first + shown - (shown ? 1 : 0), total });
  // A changed filter starts again from the first page. Replaced, not pushed: Back leaves the page
  // rather than undoing each change of a date.
  const onFiltersChange = (next: Filters) =>
    navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.billing_entries_title()}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <EntryFilters filters={filters} onChange={onFiltersChange} />
        {/* A range is always set, so an empty page means nothing matches it, not nothing yet. */}
        <WalletEntriesTable entries={entries.data} filtered caption={range} />
        <ListPagination page={page} pages={pages} />
      </CardContent>
    </Card>
  );
}
